import type { CartItem, ShipmentRate, ShippingAddressType } from "@constants/formDefaults";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import { CardElement } from "@stripe/react-stripe-js";
import type { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { formatCurrency, type SupportedCurrency } from "@utils/currencyUtils";
import type { ReactNode } from "react";

interface ConfirmUnAuthenticatedProps {
  summary: Array<{ title: string; value: ReactNode; isActive: boolean }>;
  onEdit: () => void;
  clientSecret?: string | null;
  cardError: string | null;
  onCardChange: (event: StripeCardElementChangeEvent) => void;
  totalWithTax?: number;
  selectedCurrency: SupportedCurrency;
  agreementControl: ReactNode;
}

export function buildShippingPayload(args: {
  hasShippingStep: boolean;
  shipmentRate: ShipmentRate | null;
  shippingAddress?: ShippingAddressType;
}) {
  const { hasShippingStep, shipmentRate, shippingAddress } = args;
  if (!(hasShippingStep && shipmentRate && shippingAddress)) return {};
  return {
    shipping_address: {
      ...shippingAddress,
      address2: shippingAddress.address2 || "",
    },
    shipment_rate: shipmentRate,
  };
}

export function buildBasePayloadUnauth(args: {
  uuid: string;
  items: CartItem[];
  professionalCredentials?: string | null;
  couponId?: string | null;
}) {
  const { uuid, items, professionalCredentials, couponId } = args;
  return {
    uuid,
    items,
    ...(professionalCredentials && { professional_credentials: professionalCredentials }),
    ...(couponId && { coupon_id: couponId }),
  };
}

export function buildBasePayloadAuth(args: { items: CartItem[]; couponId?: string | null }) {
  const { items, couponId } = args;
  return {
    items,
    ...(couponId && { coupon_id: couponId }),
  };
}

export function buildUpdatePayloadUnauth(args: {
  uuid: string;
  items: CartItem[];
  professionalCredentials?: string | null;
  couponId?: string | null;
  hasShippingStep: boolean;
  shipmentRate: ShipmentRate | null;
  shippingAddress?: ShippingAddressType;
}) {
  const {
    uuid,
    items,
    professionalCredentials,
    couponId,
    hasShippingStep,
    shipmentRate,
    shippingAddress,
  } = args;

  const base = buildBasePayloadUnauth({ uuid, items, professionalCredentials, couponId });
  const shipping = buildShippingPayload({
    hasShippingStep,
    shipmentRate,
    shippingAddress,
  });

  return { ...base, ...shipping };
}

export function buildUpdatePayloadAuth(args: {
  items: CartItem[];
  couponId?: string | null;
  hasShippingStep: boolean;
  shipmentRate: ShipmentRate | null;
  shippingAddress?: ShippingAddressType;
}) {
  const { items, couponId, hasShippingStep, shipmentRate, shippingAddress } = args;

  const base = buildBasePayloadAuth({ items, couponId });
  const shipping = buildShippingPayload({
    hasShippingStep,
    shipmentRate,
    shippingAddress,
  });

  return { ...base, ...shipping };
}

export default function ConfirmUnAuthenticated({
  summary,
  onEdit,
  clientSecret,
  cardError,
  onCardChange,
  totalWithTax,
  selectedCurrency,
  agreementControl,
}: ConfirmUnAuthenticatedProps) {
  return (
    <Box>
      <CardContent sx={{ px: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Stack flex={1} spacing={1}>
            {summary
              .filter((item) => item.isActive)
              .map(({ title, value }) => (
                <Box key={`${title}${String(value)}`}>
                  <Typography variant="body1" fontWeight={600}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 1 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
          </Stack>
          <Box sx={{ alignSelf: "flex-start" }}>
            <Button color="primary" size="small" onClick={onEdit}>
              Edit
            </Button>
          </Box>
        </Stack>
      </CardContent>

      <CardHeader title="Enter Your Billing Information" slotProps={{ title: { variant: "h5" } }} />

      {clientSecret && (
        <FormControl error={!!cardError} fullWidth sx={{ mt: "16px", px: 0 }}>
          <InputLabel shrink sx={{ backgroundColor: "white" }}>
            Card Details
          </InputLabel>
          <Box
            sx={{
              border: "1px solid",
              borderColor: cardError ? "error.main" : "divider",
              borderRadius: "4px",
              p: "12px",
            }}
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
              onChange={onCardChange}
            />
          </Box>
          {cardError && (
            <Stack>
              <FormHelperText>
                Sorry, but we had an issue adding your payment method.
              </FormHelperText>
              <FormHelperText>For reference: {cardError}</FormHelperText>
              <FormHelperText>
                Please try again or contact support if this issue persists.
              </FormHelperText>
            </Stack>
          )}
        </FormControl>
      )}

      <CardContent>
        {totalWithTax !== null && totalWithTax !== undefined && (
          <Typography variant="caption" mt={4} display="block" fontWeight={600}>
            You will be charged {formatCurrency(totalWithTax, selectedCurrency)} today.
          </Typography>
        )}
        <Box mt={4}>{agreementControl}</Box>
      </CardContent>
    </Box>
  );
}
